// pages/AddMemoryPage.jsx — Anamoria SPA
// v0.8 — NEW-105 (2026-09-26): cap 'The day' input at DAY_MAX = 40 chars.
// v0.7 — 2.2b: AD visual reframe, page-level (July 29, 2026)
//
// Batch 2, safe layer (Sophie 2026-07-29). Page/column changes only — the
// region components' interaction is unchanged (region-card conversion is a
// separate, reviewed decision). Changes:
//   - Prompt moves INTO the content column as a warm rounded card (warm variant,
//     no fullWidth), at the top above the eyebrow — matching the approved mockup.
//   - Mode label becomes the "Adding a memory" eyebrow (small-caps sage).
//   - Title placeholder "Name this memory" → "Give it a name", no trailing
//     period (Q1 resolved; copy pending formal v0.3 ratification).
//   - Cancel moves to the bottom, below Save.
//   - Page ground aligned to the AD warm off-white (#FBF9F3).
//
// v0.6 — 2.2a: prompt on unified capture + session Dismiss (July 29, 2026)
//
// ADDITIVE — the legacy capture pages (RecordPage/WritePage/PhotoPage/VideoPage)
// keep their prompt and are untouched; this replicates the RecordPage v5.9
// mechanics on the unified screen (decisions resolved with Sophie 2026-07-29):
//   - Fetch today's prompt alongside the space (GET /spaces/{id}/prompt,
//     tolerant of null — no prompt ⇒ no banner). Rendered via the shared
//     PromptBanner (fullWidth) between the header and the form.
//   - "Try a different prompt" → POST /spaces/{id}/prompt/advance (handleSkip).
//   - Dismiss ("Not today") is SESSION-ONLY: in-memory state, resets when the
//     page is reopened. No localStorage, no server flag.
//   - Save-through: prompt attribution added to the create body via the pure
//     buildPromptFields helper (promptText/promptTitle always when present;
//     promptId/groupPromptId when present); prompt/respond fired when promptId
//     is set. A DISMISSED prompt is NOT recorded on the memory.
//   - Flag: rides the existing moments_enabled canary (route already gated); no
//     new flag. Legacy pages are the regression guard — they must not change.
//
// v0.5 — C3: explicit Cancel in create mode (July 22, 2026)
//
// D-4.9 (plan v0.7 §2, carried into v0.8) + S-12 (copy v0.5): the live screen
// had a back-affordance only (verified 2026-07-22); an explicit "Cancel" text
// link now sits at the top of the content column (A-4 close: top-of-view
// placement, the only placement signal in the primary literature). Behaviour
// = handleBack exactly: clean screen exits to the feed immediately (O-7),
// dirty screen raises the LeaveConfirmModal. Zero new strings.
//
// v0.4 — C1: capture regions extracted (July 22, 2026)
//
// PURE RELOCATION — zero behaviour change (B-2(a) close, Fowler preparatory
// refactoring; house precedent v2.17 mapMemoryRow, 3.1-N guard/performSave).
// Executes momentedit-4-1-spa-DRAFT-for-review-v0.4.md commit C1:
//   - Write / Photo / Recording regions moved verbatim to
//     components/capture/{WriteRegion,PhotoRegion,RecordingRegion}.jsx, along
//     with the state, refs, hooks, validators, constants, strings, icons, and
//     effects that ONLY they used (each enumerated in its component header).
//   - This page keeps: title/day/nudge/visibility/save pipeline, the
//     photos/recordings arrays, ids (nextPieceId), and object-URL lifecycle;
//     regions hand finished pieces up via onAddPhoto/onAddRecording.
//   - Styles stay in AddMemoryPage.module.css (single visual source; regions
//     import it — recorded trade, draft v0.4).
//   - PlayGlyph was defined-but-unused before this change and remains so —
//     left untouched (not part of the moved behaviour).
//
// v0.3 — 3.1-N save nudge (July 21, 2026)
//
// Executes moment-phase3-feed-detail-plan-v0.4.md step 3.1-N (D-3.9, O-3);
// approved via addmemory-3-1N-save-nudge-DRAFT-for-review-v0.3.md (v0.2 §E/§F
// dispositioned as recommended + A-1, Sophie 2026-07-21). Changes from v0.2:
//   - handleSave split into guard + performSave (commit 1, pure relocation —
//     POST body verbatim), then nudge gate added after the empty-save guard
//     (commit 2): empty name → inline escapable prompt, unless O-3 stop rule
//     met (two cumulative declines, per user, utils/saveNudge.js).
//   - Nudge block renders IN PLACE of the save row (copy v0.4 §2a strings,
//     verbatim; zero string invention). Equal-weight choices, no default
//     answer, no aria-labels (WCAG 2.5.3 G211). Focus → group on show (APG
//     persistence-of-focus), → title field on "Add a name". Esc = cancel,
//     not decline (F-N5). Typing a name dismisses (F-N4).
//   - Canary-only by construction: route reachable only behind the flag.
//
// v0.2 — 2.1b POST wiring: assemble → upload → save (July 19, 2026)
//
// Executes moment-phase2-capture-plan-v0.4.md step 2.1b; approved via
// addmemorypage-2.1b-post-wiring-DRAFT-for-review-v0.5.md (decisions D-2.1b-1..4
// + copy closures O-1/O-2, all closed 2026-07-19). Changes from v0.1:
//   - handleSave real: pieces assembled in screen order (text, photos add-order,
//     recordings add-order — D-2.1b-1), sequential uploads via the legacy
//     three-step pipeline (POST /media/upload-url → putS3 — D-2.1b-3), then
//     POST /spaces/{id}/moments (anamoria-memories v2.17 contract, verified in
//     deployed-repo source). No prompt fields (2.2a).
//   - MIME normalization audio/x-m4a→audio/mp4, audio/x-wav→audio/wav before
//     presign (pairs with anamoria-media v1.7 allowlist: +audio/mpeg, +audio/wav).
//   - Interim post-save (D-2.1b-2): invalidateMemoriesCache + navigate to the
//     space feed. Replaced by the real success screen at 2.2b — condition of the
//     closed decision.
//   - Errors: save-failed line (copy v0.3 §7; state never cleared before 201);
//     TOO_MANY_PIECES → cap line; PLAN_LIMIT_REACHED deliberately stubbed through
//     the generic save-failed path (Sophie 2026-07-19; billing_enabled off for
//     beta, server check flag-gated — verified in v2.17 source).
//   - Empty-save guard (server would 400 MISSING_PIECES): N1 line, no POST.
//   - Library/photo rejection strings replaced with the ratified N-set
//     (Anamoria_AddMemory_Errors_Limits_Copy_Revisions_v0.1.md + draft v0.5
//     closures; trailing-periods rule O-2: full-sentence errors keep periods).
//   - Photo region gains an inline error line (N2 over-size — was silent —
//     and N5 unsupported type).
//
// v0.1 — 2.1a scaffold: unified "Add a memory" capture screen (July 19, 2026)
//
// Executes moment-phase2-capture-plan-v0.4.md step 2.1a; approved via
// addmemorypage-2.1a-scaffold-DRAFT-for-review-v0.2.md (2026-07-19). Dark: route only, no entry links (entry + footer = 2.1c, flag-gated).
// POST wiring, uploads, and error paths = 2.1b. Zero edits to the four legacy
// capture pages (RecordPage/WritePage/PhotoPage/VideoPage untouched).
//
// Provenance (per element — AD spec = hearth-capture-detail-feed-specs.md §1):
//   - Visual/structural baseline: AD spec §1.4–1.9 (single column, always-visible
//     region labels, dashed border-dissolution mechanic, serif title, free-form
//     day), §1.15 motion, §1.16 accessibility (fieldset/legend, aria-labels,
//     keyboard order), §6 tokens.
//   - Overrides of the AD spec, from closed decisions (see the DRAFT-for-review
//     doc §2 discrepancy table): Voice|Video in-app recording region (D2,
//     R1–R8 — AD's audio-file picker + video omission is the known
//     anonymization artifact); strings per Anamoria_AddMemory_Capture_Copy
//     v0.3 (build labels "Write"/"Photo" + no-trailing-periods style pending
//     v0.3 read-aloud ratification); visibility toggle
//     (D3, absent from AD); NO draft auto-save (D4 — LeaveConfirm parity only;
//     AD §1.14 not built); title cap 120 (D5; AD said 200); pieces collapse to
//     chips, up to MAX_PIECES=10 (R8/D5/v2.17; AD assumed one piece per region);
//     LeaveConfirmModal reused (settled component) instead of AD §1.13 inline
//     confirm; field order name→day→regions→visibility→save (plan v0.4 §3
//     step 2.1a; AD put the day last).
//   - Re-homed unchanged: useRecorder (RecordPage pattern), useVideoRecorder
//     (VideoPage pattern), photo select pipeline values (PhotoPage v2.9:
//     10 MB / jpeg+png+webp), LovedOneBar header + space fetch (WritePage v1.6
//     pattern), LeaveConfirmModal v1.0.
//
// Changes from —: initial version. Built on branch feat/addmemory-scaffold-2-1a
// with Sophie's live design/copy review of 2026-07-19 folded in (ground, lines,
// weights, expand-in-place day, library uploads, copy v0.3 alignment); the
// decision trail is in session log 2026-07-19 + capture copy v0.3.

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { createApiClient } from '../api/client';
import { invalidateMemoriesCache } from '../components/MemoryFeed';
import { useResolvedPhotoUrl } from '../hooks/useResolvedPhotoUrl';
import { shouldNudge, bumpNudgeDeclines } from '../utils/saveNudge';
import { buildPromptFields } from '../utils/promptFields';
import WriteRegion from '../components/capture/WriteRegion';
import PhotoRegion from '../components/capture/PhotoRegion';
import RecordingRegion from '../components/capture/RecordingRegion';
import RegionCard, { DayIcon } from '../components/capture/RegionCard';
import PromptBanner from '../components/PromptBanner';
import LovedOneBar from '../components/LovedOneBar';
import LeaveConfirmModal from '../components/LeaveConfirmModal';
import ButterflyLoader from '../components/ButterflyLoader';
import styles from './AddMemoryPage.module.css';
import { useAppContext } from '../App';

// D5 / anamoria-memories v2.17 contract: a moment holds at most 10 pieces.
const MAX_PIECES = 10;
// D5: client-side title cap 120 (server value VERIFY at 2.1b — align, don't fork).
const TITLE_MAX = 120;
const DAY_MAX = 40;
// C1: photo/audio/video pipeline constants, validators, and their N-strings
// moved verbatim into components/capture/{PhotoRegion,RecordingRegion}.jsx.

// 2.1b: base MIME (strip codecs) + normalize browser x- variants to the
// canonical types the anamoria-media v1.7 allowlist accepts (D-2.1b-4).
function normalizeMime(type, fallback) {
  const base = (type || fallback || '').split(';')[0];
  if (base === 'audio/x-m4a') return 'audio/mp4';
  if (base === 'audio/x-wav') return 'audio/wav';
  return base;
}

// Ratified error strings (copy revisions v0.1 + draft v0.5 closures).
const SAVE_FAILED_LINE =
  'We weren’t able to save this memory. Nothing you added has been lost. When you’re ready, please try again.';
const CAP_LINE =
  'A memory can hold up to 10 things. You can save this one and add more in a new memory.';
const EMPTY_SAVE_LINE =
  'There’s nothing in this memory yet. When you’re ready, you can add some words, a photo, or a recording.';
const PHOTO_TOO_LARGE_LINE =
  'We weren’t able to add this photo because the file is very large. A version under 10 MB will work.';
const PHOTO_WRONG_TYPE_LINE =
  'We can’t add this kind of photo. JPEG, PNG, or WebP photos will work.';

// 3.1-N (D-3.9; copy v0.4 §2a — ratified strings, verbatim)
const NUDGE_PROMPT =
  'Would you like to name this memory? It can help you find it again later.';
const NUDGE_ADD_LABEL = 'Add a name';
const NUDGE_SAVE_LABEL = 'Save without a name';

let nextPieceId = 1;

export default function AddMemoryPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently, user } = useAuth0();
  // W3 inc2: when text_as_object_enabled, upload a text memory's words to S3 (Option A); else inline note.
  const appState = useAppContext();
  const textAsObjectEnabled = appState?.flags?.text_as_object_enabled === true;

  // ─── Space data (WritePage v1.6 pattern) ───
  const [space, setSpace] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const resolvedPhotoUrl = useResolvedPhotoUrl(space, getAccessTokenSilently);

  // ─── Prompt (2.2a) — fetched with the space; RecordPage v5.9 mechanics ───
  const [prompt, setPrompt] = useState(null);
  // Session-only Dismiss: resets on remount (no persistence, resolved 2026-07-29).
  const [promptDismissed, setPromptDismissed] = useState(false);

  // ─── Form state ───
  const [title, setTitle] = useState('');
  const [day, setDay] = useState('');            // free-form text, never parsed (AD §1.9 R4; copy v0.2 §2)
  const [text, setText] = useState('');          // single written piece
  const [photos, setPhotos] = useState([]);      // [{ id, file, url }]
  const [recordings, setRecordings] = useState([]); // [{ id, kind:'voice'|'video', blob, mimeType, duration, durationFormatted, url }]

  // NEW-130: split regions each need their own filtered slice; recordings
  // itself stays one flat array (unchanged) so add/remove/upload logic below
  // doesn't need to know about the split at all.
  const voiceRecordings = recordings.filter((r) => r.kind === 'voice');
  const videoRecordings = recordings.filter((r) => r.kind === 'video');

  // NEW-130: VoiceCard's fallback path (used only if playbackUrl is somehow
  // missing, which it never is here) needs a getApi — same pattern as
  // MomentDetailPage's own.
  const getApi = useCallback(
    () => createApiClient(getAccessTokenSilently),
    [getAccessTokenSilently]
  );

  // ─── UI state ───
  // C1: region-internal UI state (mode, write expansion, chip confirm/playback,
  // pickers, inline errors) moved into components/capture/* with the regions.
  const [dayExpanded, setDayExpanded] = useState(false);     // expand-in-place day (Sophie 2026-07-19)
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // 2.1b save state: 'idle' | 'saving'; notice: null | 'empty' | 'failed' | 'cap'
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  // 3.1-N: inline save nudge (D-3.9); replaces the save row while shown
  const [showNudge, setShowNudge] = useState(false);
  const nudgeRef = useRef(null);       // focus target on show (F-N6; APG persistence-of-focus)
  const titleInputRef = useRef(null);  // "Add a name" focus target

  const dayInputRef = useRef(null);
  // Title underline hugs the text (Sophie 2026-07-19): hidden span measures the
  // rendered width of value/placeholder; input width follows it.
  const titleMeasureRef = useRef(null);
  const [titleWidth, setTitleWidth] = useState(null);

  const pieceCount = photos.length + recordings.length + (text.trim() ? 1 : 0);
  const atCap = pieceCount >= MAX_PIECES;
  const dirty =
    title.trim() !== '' || day.trim() !== '' || text.trim() !== '' ||
    photos.length > 0 || recordings.length > 0;

  // ─── Fetch space + today's prompt on mount (2.2a; RecordPage v5.9 pattern) ───
  // The prompt is best-effort: a null/failed prompt simply renders no banner and
  // never blocks capture (.catch(() => null), like RecordPage).
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const api = createApiClient(getAccessTokenSilently);
        const [spaceData, promptData] = await Promise.all([
          api.get(`/spaces/${spaceId}`),
          api.get(`/spaces/${spaceId}/prompt`).catch(() => null),
        ]);
        if (!cancelled) {
          setSpace(spaceData);
          setPrompt(promptData);
        }
      } catch (err) {
        console.error('AddMemoryPage load error:', err);
        if (!cancelled) setLoadError('Failed to load. Please go back and try again.');
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, getAccessTokenSilently]);

  // ─── Prompt skip / dismiss (2.2a) ───
  // "Try a different prompt" → advance to the next prompt (RecordPage parity).
  const handleSkipPrompt = useCallback(async () => {
    try {
      const api = createApiClient(getAccessTokenSilently);
      const next = await api.post(`/spaces/${spaceId}/prompt/advance`);
      if (next) setPrompt(next);
    } catch (err) {
      console.error('AddMemoryPage skip prompt error:', err);
    }
  }, [getAccessTokenSilently, spaceId]);

  // "Not today" → set the prompt aside for this capture session only.
  const handleDismissPrompt = useCallback(() => setPromptDismissed(true), []);

  // C1: recording collection, camera preview, and chip playback moved into
  // RecordingRegion; finished pieces arrive via addRecording below.

  // ─── Title underline width tracks the text ───
  useLayoutEffect(() => {
    if (titleMeasureRef.current) {
      setTitleWidth(titleMeasureRef.current.offsetWidth + 8); // +8px caret room
    }
  }, [title]);

  // ─── Revoke object URLs on unmount (C1: playback pause moved to the region) ───
  useEffect(() => {
    return () => {
      setRecordings((prev) => { prev.forEach((r) => URL.revokeObjectURL(r.url)); return prev; });
      setPhotos((prev) => { prev.forEach((p) => URL.revokeObjectURL(p.url)); return prev; });
    };
  }, []);

  // ─── Handlers ───

  const handleBack = useCallback(() => {
    if (dirty) { setShowLeaveConfirm(true); return; }
    navigate(`/spaces/${spaceId}`);
  }, [dirty, navigate, spaceId]);

  const handleTitleChange = (e) => {
    if (showNudge) setShowNudge(false); // F-N4: typing a name answers the question
    if (e.target.value.length <= TITLE_MAX) setTitle(e.target.value);
  };

  // C1: validation and picker mechanics live in the regions; these callbacks
  // preserve the v0.3 division of labour — the page owns arrays, ids, and
  // object URLs. Guard order matches v0.3 exactly (atCap/saving checked in
  // PhotoRegion before validation; atCap in RecordingRegion's handlers).
  const addPhoto = (file) => {
    setPhotos((prev) => [...prev, { id: nextPieceId++, file, url: URL.createObjectURL(file) }]);
  };

  const addRecording = (data) => {
    setRecordings((prev) => [...prev, {
      id: nextPieceId++,
      ...data,
      url: URL.createObjectURL(data.blob),
    }]);
  };

  const removePhoto = (id) => {
    setPhotos((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  const removeRecording = (id) => {
    setRecordings((prev) => {
      const gone = prev.find((r) => r.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return prev.filter((r) => r.id !== id);
    });
  };

  // 2.1b: assemble → upload → POST /spaces/{id}/moments (v2.17, verified in
  // deployed-repo source). Screen-order pieces (D-2.1b-1); sequential uploads
  // (D-2.1b-3); interim post-save nav (D-2.1b-2, replaced at 2.2b).
  // PLAN_LIMIT_REACHED deliberately falls through to the generic save-failed
  // path (stub decision, Sophie 2026-07-19). State is never cleared before the
  // 201, so "Nothing you added has been lost" is true by construction.
  const handleSave = async () => {
    if (saving) return;
    setSaveNotice(null);
    if (!dirty) return;
    if (pieceCount === 0) {
      // Server would 400 MISSING_PIECES — pre-empt with the N1 line, no POST.
      setSaveNotice('empty');
      return;
    }
    // 3.1-N nudge gate (D-3.9): empty name → one escapable inline prompt,
    // unless the O-3 stop rule has been met. Runs after the empty-save guard
    // (an empty memory is never nudged toward a name).
    if (!title.trim() && shouldNudge(user?.sub)) {
      setShowNudge(true);
      return;
    }
    await performSave();
  };

  // 3.1-N commit 1: pure relocation of the 2.1b save body (guard/performSave
  // split, draft v0.3 §E-7) — zero behaviour change in this commit.
  const performSave = async () => {
    setSaving(true);
    try {
      const api = createApiClient(getAccessTokenSilently);

      const pieces = [];
      if (text.trim()) {
        if (textAsObjectEnabled) {
          const t = await api.post('/media/upload-url', { spaceId, mimeType: 'text/plain', mediaType: 'text' });
          await api.putS3(t.uploadUrl, new Blob([text.trim()], { type: 'text/plain' }), 'text/plain');
          pieces.push({ type: 'text', textContentKey: t.s3Key });
        } else {
          pieces.push({ type: 'text', note: text.trim() });
        }
      }
      for (const p of photos) {
        const mimeType = normalizeMime(p.file.type, 'image/jpeg');
        const uploadData = await api.post('/media/upload-url', { spaceId, mimeType, mediaType: 'photo' });
        await api.putS3(uploadData.uploadUrl, p.file, mimeType);
        pieces.push({ type: 'photo', s3Key: uploadData.s3Key });
      }
      for (const r of recordings) {
        const isVoice = r.kind === 'voice';
        const mimeType = normalizeMime(r.mimeType, isVoice ? 'audio/webm' : 'video/webm');
        const uploadData = await api.post('/media/upload-url', {
          spaceId, mimeType, mediaType: isVoice ? 'voice' : 'video',
        });
        await api.putS3(uploadData.uploadUrl, r.blob, mimeType);
        pieces.push({
          type: isVoice ? 'voice' : 'video',
          s3Key: uploadData.s3Key,
          mimeType,
          duration: Math.round(r.duration),
        });
      }

      // 2.2a: attribute the prompt on the memory. buildPromptFields returns {}
      // when there's no prompt or the person dismissed it (a set-aside prompt is
      // never recorded). Contract mirrors RecordPage v5.9.
      const promptFields = buildPromptFields(prompt, promptDismissed);

      await api.post(`/spaces/${spaceId}/moments`, {
        title: title.trim() || null,
        dayText: day.trim() || null,
        isPrivate: true, // always private at create - sharing is a deliberate, per-piece choice on the edit screen
        pieces,
        ...promptFields,
      });

      // 2.2a: mark the prompt responded (only when there's a promptId to respond
      // to; custom group prompts have none). Fire-and-forget, like RecordPage.
      if (promptFields.promptId) {
        api.post(`/spaces/${spaceId}/prompt/respond`, { promptId: promptFields.promptId }).catch(() => {});
      }

      invalidateMemoriesCache(spaceId);
      navigate(`/spaces/${spaceId}`);
    } catch (err) {
      console.error('AddMemoryPage save error:', err);
      setSaving(false);
      setSaveNotice(err?.error === 'TOO_MANY_PIECES' ? 'cap' : 'failed');
    }
  };

  // 3.1-N: focus the group on show — the Save button the person clicked has
  // unmounted (APG: unmanaged removal drops focus to body); neither choice is
  // pre-focused (equal weight — NN/g: no default answer). F-N6.
  // Esc listens at DOCUMENT level while shown (gate fix, Sophie 2026-07-21:
  // container-scoped keydown went dead once focus left the group). F-N5:
  // cancel, not decline.
  useEffect(() => {
    if (!showNudge) return undefined;
    nudgeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') setShowNudge(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showNudge]);

  const handleNudgeAddName = () => {
    setShowNudge(false);
    titleInputRef.current?.focus();
  };
  const handleNudgeSaveWithout = async () => {
    bumpNudgeDeclines(user?.sub); // O-3: only this path counts
    setShowNudge(false);
    await performSave();
  };

  // ─── Render ───

  if (loadError) {
    return (
      <div className={styles.page}>
        <p className={styles.loadError}>{loadError}</p>
      </div>
    );
  }
  if (!space) return <ButterflyLoader />;

  const lovedOneName = space.name;

  return (
    <div className={styles.page}>
      {/* Settled capture-screen header (WritePage v1.6 header-consistency).
          AD §4.1 top bar belongs to the Hearth internal chrome — adopted only
          when the internal design system goes global (draft doc §2, row 8). */}
      <LovedOneBar
        spaceName={space.name}
        spacePhotoUrl={resolvedPhotoUrl}
        subtitle="Add a memory"
        onBack={handleBack}
      />

      <main className={styles.content}>
        {/* 2.2b: today's prompt as a warm rounded card at the top of the column
            (AD mockup). Dismiss ("Not today") is session-only — the
            !promptDismissed gate unmounts it for the rest of this capture. */}
        {prompt && !promptDismissed && (
          <PromptBanner
            prompt={prompt}
            onSkip={handleSkipPrompt}
            showSkip
            skipLabel="Try something else"
            onDismiss={handleDismissPrompt}
            showDismiss
            warm
          />
        )}

        {/* 2.2b: "Adding a memory" eyebrow (AD structure). Was the .modeLabel
            "Add a memory"; restyled to a small-caps sage eyebrow. */}
        <p className={styles.eyebrow}>Adding a memory</p>

        {/* ── Name (serif treatment AD §1.6; cap D5). String changed by Sophie
            2026-07-19: AD's "Give it a name" replaces copy v0.2 §2's "Name this
            memory, if you like" — logged for copy v0.3 ratification (note: AD
            mockup carries a trailing period; Sophie's instruction had none —
            built without, flag if wrong). Centered, underline hugs the text. ── */}
        <div className={`${styles.titleWrap} ${styles.enter1}`}>
          <label className={styles.hiddenLabel} htmlFor="am-title">Give it a name</label>
          <span ref={titleMeasureRef} className={styles.titleMeasure} aria-hidden="true">
            {title || 'Give it a name'}
          </span>
          <input
            id="am-title"
            ref={titleInputRef}
            className={styles.titleField}
            type="text"
            value={title}
            onChange={handleTitleChange}
            placeholder="Give it a name"
            aria-label="Memory title"
            maxLength={TITLE_MAX}
            style={titleWidth ? { width: `${titleWidth}px` } : undefined}
          />
        </div>

        {/* ── The day (2.2b: AD card; free-form per AD §1.9 + copy v0.2 §2) ── */}
        <fieldset className={`${styles.region} ${styles.enter2}`}>
          <legend className={styles.hiddenLabel}>The day</legend>
          <RegionCard
            icon={<DayIcon />}
            title="The day"
            helper="If you'd like, you can note the day. You can skip this."
            open={dayExpanded || day.trim() !== ''}
            onToggle={() => { setDayExpanded(true); setTimeout(() => dayInputRef.current?.focus(), 50); }}
          >
            <input
              ref={dayInputRef}
              className={styles.dayField}
              type="text"
              maxLength={DAY_MAX}
              value={day}
              onChange={(e) => setDay(e.target.value)}
              onBlur={() => { if (!day.trim()) setDayExpanded(false); }}
              placeholder="Write it any way you remember it"
              aria-label="When was this? Optional."
              autoComplete="off"
              autoCapitalize="none"
              spellCheck="false"
            />
          </RegionCard>
        </fieldset>

        {/* ── Write region (R7 compact/expand; R8 chip; copy v0.2 §3) — C1: extracted ── */}
        <WriteRegion
          text={text}
          onChange={setText}
          atCap={atCap}
          className={styles.enter3}
        />

        {/* ── Photo region (R8 chips; PhotoPage v2.9 pipeline values) — C1: extracted ── */}
        <PhotoRegion
          photos={photos}
          onAddPhoto={addPhoto}
          onRemovePhoto={removePhoto}
          atCap={atCap}
          saving={saving}
          className={styles.enter4}
        />

        {/* ── Recording regions: split, voice + video (NEW-130) — each renders
            its own real per-piece preview (VoiceCard/VideoCard), record/pause/
            stop, library picker, and hidden input; atCap/onAdd/onRemove are the
            same flat `recordings` array + handlers as before, just filtered ── */}
        <RecordingRegion
          kind="voice"
          recordings={voiceRecordings}
          onAddRecording={addRecording}
          onRemoveRecording={removeRecording}
          atCap={atCap}
          saving={saving}
          getApi={getApi}
          className={styles.enter5}
        />
        <RecordingRegion
          kind="video"
          recordings={videoRecordings}
          onAddRecording={addRecording}
          onRemoveRecording={removeRecording}
          atCap={atCap}
          saving={saving}
          className={styles.enter5}
        />

        {/* ── Piece cap notice (copy v0.2 §7; client pre-empt of v2.17 400) ── */}
        {atCap && (
          <p className={`${styles.capLine} ${styles.enter5}`}>
            A memory can hold up to 10 things. You can save this one and add more in a new memory
          </p>
        )}

        <p className={`${styles.privacyNote} ${styles.enter6}`}>Memories are private by default and you can choose to share them later.</p>

        {/* ── One Save, always enabled (R9; AD §1.11; copy v0.2 §7).
            2.1b: Saving… state (copy v0.3 §7) + notice lines (N1 empty-save;
            save-failed; cap). Notices use the ratified full-sentence-error
            register (O-2: periods kept). ── */}
        {/* 3.1-N: nudge renders IN PLACE of the save row (copy v0.4 §2a). */}
        {showNudge ? (
          <div
            className={`${styles.nudgeBlock} ${styles.enter7}`}
            role="group"
            aria-labelledby="am-nudge-prompt"
            tabIndex={-1}
            ref={nudgeRef}
          >
            <p id="am-nudge-prompt" className={styles.nudgePrompt}>{NUDGE_PROMPT}</p>
            <div className={styles.nudgeChoices}>
              {/* No aria-label on either button — visible text IS the accessible
                  name (WCAG 2.5.3 G211; aria-label would override it). F-N8. */}
              <button type="button" className={styles.nudgeBtn} onClick={handleNudgeAddName}>
                {NUDGE_ADD_LABEL}
              </button>
              <button type="button" className={styles.nudgeBtn} onClick={handleNudgeSaveWithout}>
                {NUDGE_SAVE_LABEL}
              </button>
            </div>
          </div>
        ) : (
        <div className={`${styles.saveBlock} ${styles.enter7}`}>
          {saveNotice === 'empty' && pieceCount === 0 && (
            <p className={styles.permissionLine}>{EMPTY_SAVE_LINE}</p>
          )}
          {saveNotice === 'failed' && (
            <p className={styles.permissionLine} role="alert">{SAVE_FAILED_LINE}</p>
          )}
          {saveNotice === 'cap' && (
            <p className={styles.permissionLine} role="alert">{CAP_LINE}</p>
          )}
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
            aria-label="Save this memory"
          >
            {saving ? 'Saving…' : 'Save this memory'}
          </button>
        </div>
        )}

        {/* 2.2b: Cancel moved to the bottom, below Save (Sophie 2026-07-29).
            Behaviour unchanged: clean = immediate exit (O-7); dirty raises the
            LeaveConfirmModal (handleBack). */}
        <button type="button" className={styles.cancelBottom} onClick={handleBack}>
          Cancel
        </button>
      </main>

      {/* D4: LeaveConfirm parity only — no draft auto-save (AD §1.14 not built). */}
      <LeaveConfirmModal
        open={showLeaveConfirm}
        message="Your memory won't be saved if you leave now."
        icon="edit"
        onKeepEditing={() => setShowLeaveConfirm(false)}
        onLeave={() => { setShowLeaveConfirm(false); navigate(`/spaces/${spaceId}`); }}
      />
    </div>
  );
}

/* C1: WAVE_HEIGHTS, MicIcon, CamIcon, PauseGlyph moved with the recording
   region into components/capture/RecordingRegion.jsx. */

function PlayGlyph() {
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true">
      <path d="M2 1l9 5-9 5z" fill="currentColor" />
    </svg>
  );
}
